import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Calculator, FileText, TrendingUp, Download, Euro, Receipt, Calendar,
  LogOut, RefreshCcw, Filter, AlertTriangle, Users, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  draft: 'Brouillon',
  sent: 'Envoye',
  pending: 'En attente',
  paid: 'Payee',
  cancelled: 'Annulee',
  accepted: 'Accepte',
  refused: 'Refuse',
  converted: 'Converti',
  expired: 'Expire',
};

const STATUS_COLORS = {
  paid: 'bg-green-600',
  sent: 'bg-blue-600',
  pending: 'bg-yellow-600',
  draft: 'bg-zinc-600',
  cancelled: 'bg-red-600',
  accepted: 'bg-green-600',
  refused: 'bg-red-600',
  converted: 'bg-purple-600',
  expired: 'bg-zinc-600',
};

export default function ComptabilitePage() {
  const { user, token, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [vatSummary, setVatSummary] = useState(null);
  const [unpaid, setUnpaid] = useState({ invoices: [], total_amount: 0, overdue_amount: 0, count: 0 });
  const [topClients, setTopClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    status: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/connexion'); return; }
    if (user.role !== 'admin' && user.role !== 'accountant') {
      toast.error('Acces comptabilite requis');
      navigate('/');
    }
  }, [authLoading, user, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.status) params.status = filters.status;

      const [dashRes, invRes, quoteRes, vatRes, unpaidRes, topRes] = await Promise.all([
        axios.get(`${API}/accounting/dashboard`, { headers }),
        axios.get(`${API}/accounting/invoices`, { headers, params }),
        axios.get(`${API}/accounting/quotes`, { headers, params }),
        axios.get(`${API}/accounting/vat-summary`, { headers }),
        axios.get(`${API}/accounting/unpaid-invoices`, { headers }),
        axios.get(`${API}/accounting/top-clients?limit=15`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setInvoices(invRes.data);
      setQuotes(quoteRes.data);
      setVatSummary(vatRes.data);
      setUnpaid(unpaidRes.data);
      setTopClients(topRes.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'accountant')) {
      fetchData();
    }
  }, [user, fetchData]);

  const downloadCsv = async (endpoint, filename) => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.set('start_date', filters.start_date);
      if (filters.end_date) params.set('end_date', filters.end_date);
      if (filters.status) params.set('status', filters.status);
      const url = `${API}/accounting/export/${endpoint}${params.toString() ? '?' + params.toString() : ''}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
      toast.success('Export telecharge');
    } catch (e) {
      toast.error('Erreur export');
    }
  };

  const fmt = (val) => `${Number(val || 0).toFixed(2)} EUR`;

  if (authLoading || !user) {
    return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-400">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator className="text-red-500" size={28} />
            <div>
              <h1 className="text-xl font-bold">Comptabilite</h1>
              <p className="text-xs text-zinc-500">Connecte en tant que {user.name} ({user.email})</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-zinc-700" onClick={fetchData} data-testid="accounting-refresh">
              <RefreshCcw size={16} className="mr-2" /> Actualiser
            </Button>
            <Button variant="outline" className="border-red-700 text-red-400 hover:bg-red-900/30" onClick={() => { logout(); navigate('/'); }} data-testid="accounting-logout">
              <LogOut size={16} className="mr-2" /> Deconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* KPI cards */}
        {dashboard && (
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-green-900/40 to-zinc-900 border-green-800/50" data-testid="kpi-day">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-zinc-400 text-sm">CA aujourd'hui</p>
                  <TrendingUp className="text-green-400" size={20} />
                </div>
                <p className="text-3xl font-bold text-white">{fmt(dashboard.revenue.day.total_ttc)}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {dashboard.revenue.day.invoices} factures payees - HT {fmt(dashboard.revenue.day.total_ht)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-900/40 to-zinc-900 border-blue-800/50" data-testid="kpi-month">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-zinc-400 text-sm">CA du mois</p>
                  <Calendar className="text-blue-400" size={20} />
                </div>
                <p className="text-3xl font-bold text-white">{fmt(dashboard.revenue.month.total_ttc)}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {dashboard.revenue.month.invoices} factures - HT {fmt(dashboard.revenue.month.total_ht)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-900/40 to-zinc-900 border-purple-800/50" data-testid="kpi-year">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-zinc-400 text-sm">CA de l'annee</p>
                  <Euro className="text-purple-400" size={20} />
                </div>
                <p className="text-3xl font-bold text-white">{fmt(dashboard.revenue.year.total_ttc)}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {dashboard.revenue.year.invoices} factures - HT {fmt(dashboard.revenue.year.total_ht)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Counters row */}
        {dashboard && (
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <p className="text-zinc-400 text-sm">Factures</p>
                <div className="flex items-end justify-between mt-1">
                  <p className="text-2xl font-bold text-white">{dashboard.invoices.total}</p>
                  <div className="text-right text-xs">
                    <p className="text-green-400">{dashboard.invoices.paid} payees</p>
                    <p className="text-yellow-400">{dashboard.invoices.pending} en attente ({fmt(dashboard.invoices.pending_amount_ttc)})</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <p className="text-zinc-400 text-sm">Devis</p>
                <div className="flex items-end justify-between mt-1">
                  <p className="text-2xl font-bold text-white">{dashboard.quotes.total}</p>
                  <div className="text-right text-xs">
                    <p className="text-blue-400">{dashboard.quotes.sent} envoyes</p>
                    <p className="text-green-400">{dashboard.quotes.accepted} acceptes</p>
                    <p className="text-purple-400">{dashboard.quotes.converted} convertis</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4 space-y-2">
                <p className="text-zinc-400 text-sm flex items-center gap-2"><Download size={14} /> Exports rapides</p>
                <div className="flex flex-col gap-2">
                  <Button size="sm" variant="outline" className="border-zinc-700 justify-start" onClick={() => downloadCsv('revenue.csv', 'chiffre_affaires.csv')} data-testid="export-revenue">
                    <Download size={14} className="mr-2" /> CA par jour (CSV)
                  </Button>
                  <Button size="sm" variant="outline" className="border-zinc-700 justify-start" onClick={() => downloadCsv('invoices.csv', 'factures.csv')} data-testid="export-invoices">
                    <Download size={14} className="mr-2" /> Factures (CSV)
                  </Button>
                  <Button size="sm" variant="outline" className="border-zinc-700 justify-start" onClick={() => downloadCsv('quotes.csv', 'devis.csv')} data-testid="export-quotes">
                    <Download size={14} className="mr-2" /> Devis (CSV)
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 text-zinc-300">
              <Filter size={16} /> <span className="font-medium">Filtres</span>
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Date debut</Label>
                <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="bg-zinc-800 border-zinc-700" data-testid="filter-start" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Date fin</Label>
                <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="bg-zinc-800 border-zinc-700" data-testid="filter-end" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Statut</Label>
                <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="filter-status"><SelectValue placeholder="Tous" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="paid">Payee</SelectItem>
                    <SelectItem value="sent">Envoye</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="cancelled">Annulee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={fetchData} className="w-full bg-red-600 hover:bg-red-700" data-testid="apply-filters">
                  Appliquer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs factures / devis / TVA / impayees / clients */}
        <Tabs defaultValue="invoices">
          <TabsList className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto">
            <TabsTrigger value="invoices" className="data-[state=active]:bg-red-600" data-testid="tab-invoices">
              <Receipt size={14} className="mr-2" /> Factures ({invoices.length})
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-red-600" data-testid="tab-quotes">
              <FileText size={14} className="mr-2" /> Devis ({quotes.length})
            </TabsTrigger>
            <TabsTrigger value="vat" className="data-[state=active]:bg-red-600" data-testid="tab-vat">
              <BarChart3 size={14} className="mr-2" /> TVA mensuelle
            </TabsTrigger>
            <TabsTrigger value="unpaid" className="data-[state=active]:bg-red-600" data-testid="tab-unpaid">
              <AlertTriangle size={14} className="mr-2" /> Impayees ({unpaid.count})
            </TabsTrigger>
            <TabsTrigger value="clients" className="data-[state=active]:bg-red-600" data-testid="tab-clients">
              <Users size={14} className="mr-2" /> Top clients
            </TabsTrigger>
          </TabsList>

          <TabsContent value="invoices" className="mt-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Factures</CardTitle>
                <Button size="sm" variant="outline" className="border-zinc-700" onClick={() => downloadCsv('invoices.csv', 'factures.csv')}>
                  <Download size={14} className="mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8 text-zinc-500">Chargement...</p>
                ) : invoices.length === 0 ? (
                  <p className="text-center py-8 text-zinc-500">Aucune facture</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium">N facture</th>
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium">Date</th>
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium">Client</th>
                          <th className="text-right py-2 px-3 text-zinc-400 font-medium">Total HT</th>
                          <th className="text-right py-2 px-3 text-zinc-400 font-medium">Total TTC</th>
                          <th className="text-center py-2 px-3 text-zinc-400 font-medium">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv) => {
                          const ttc = Number(inv.total || 0);
                          return (
                            <tr key={inv.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                              <td className="py-2 px-3 font-mono text-white">{inv.invoice_number || inv.id?.slice(0, 8)}</td>
                              <td className="py-2 px-3 text-zinc-300">{(inv.created_at || '').slice(0, 10)}</td>
                              <td className="py-2 px-3 text-zinc-300">
                                <p>{inv.user_name}</p>
                                <p className="text-zinc-500 text-xs">{inv.user_email}</p>
                              </td>
                              <td className="py-2 px-3 text-right text-zinc-300">{(ttc / 1.2).toFixed(2)}</td>
                              <td className="py-2 px-3 text-right text-white font-bold">{ttc.toFixed(2)}</td>
                              <td className="py-2 px-3 text-center">
                                <Badge className={`${STATUS_COLORS[inv.status] || 'bg-zinc-600'} text-white`}>
                                  {STATUS_LABELS[inv.status] || inv.status}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quotes" className="mt-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Devis</CardTitle>
                <Button size="sm" variant="outline" className="border-zinc-700" onClick={() => downloadCsv('quotes.csv', 'devis.csv')}>
                  <Download size={14} className="mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8 text-zinc-500">Chargement...</p>
                ) : quotes.length === 0 ? (
                  <p className="text-center py-8 text-zinc-500">Aucun devis</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium">N devis</th>
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium">Date</th>
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium">Client</th>
                          <th className="text-right py-2 px-3 text-zinc-400 font-medium">Total HT</th>
                          <th className="text-right py-2 px-3 text-zinc-400 font-medium">Total TTC</th>
                          <th className="text-center py-2 px-3 text-zinc-400 font-medium">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quotes.map((q) => {
                          const ttc = Number(q.total || 0);
                          return (
                            <tr key={q.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                              <td className="py-2 px-3 font-mono text-white">{q.quote_number || q.id?.slice(0, 8)}</td>
                              <td className="py-2 px-3 text-zinc-300">{(q.created_at || '').slice(0, 10)}</td>
                              <td className="py-2 px-3 text-zinc-300">
                                <p>{q.client_name || q.user_name}</p>
                                <p className="text-zinc-500 text-xs">{q.client_email || q.user_email}</p>
                              </td>
                              <td className="py-2 px-3 text-right text-zinc-300">{(ttc / 1.2).toFixed(2)}</td>
                              <td className="py-2 px-3 text-right text-white font-bold">{ttc.toFixed(2)}</td>
                              <td className="py-2 px-3 text-center">
                                <Badge className={`${STATUS_COLORS[q.status] || 'bg-zinc-600'} text-white`}>
                                  {STATUS_LABELS[q.status] || q.status}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="vat" className="mt-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">TVA collectee {vatSummary?.year}</CardTitle>
                <Button size="sm" variant="outline" className="border-zinc-700" onClick={() => downloadCsv('vat.csv', `tva_${vatSummary?.year}.csv`)} data-testid="export-vat">
                  <Download size={14} className="mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {!vatSummary ? <p className="text-zinc-500 text-center py-8">Chargement...</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-2 px-3 text-zinc-400">Mois</th>
                          <th className="text-right py-2 px-3 text-zinc-400">Nb factures</th>
                          <th className="text-right py-2 px-3 text-zinc-400">Total HT</th>
                          <th className="text-right py-2 px-3 text-zinc-400">TVA collectee</th>
                          <th className="text-right py-2 px-3 text-zinc-400">Total TTC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vatSummary.months.map((m) => (
                          <tr key={m.month} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                            <td className="py-2 px-3 text-zinc-300">{m.month_name}</td>
                            <td className="py-2 px-3 text-right text-zinc-400">{m.count}</td>
                            <td className="py-2 px-3 text-right text-zinc-300">{m.ht.toFixed(2)} EUR</td>
                            <td className="py-2 px-3 text-right text-yellow-400 font-semibold">{m.tva.toFixed(2)} EUR</td>
                            <td className="py-2 px-3 text-right text-white font-bold">{m.ttc.toFixed(2)} EUR</td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-red-600 bg-zinc-800/40">
                          <td className="py-3 px-3 font-bold text-white">TOTAL {vatSummary.year}</td>
                          <td className="py-3 px-3 text-right text-zinc-300">{vatSummary.totals.count}</td>
                          <td className="py-3 px-3 text-right text-zinc-200 font-semibold">{vatSummary.totals.ht.toFixed(2)} EUR</td>
                          <td className="py-3 px-3 text-right text-yellow-300 font-bold">{vatSummary.totals.tva.toFixed(2)} EUR</td>
                          <td className="py-3 px-3 text-right text-white font-bold text-lg">{vatSummary.totals.ttc.toFixed(2)} EUR</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="unpaid" className="mt-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Factures impayees</span>
                  <span className="text-sm font-normal">
                    Total <span className="text-yellow-400 font-bold">{fmt(unpaid.total_amount)}</span>
                    {unpaid.overdue_amount > 0 && <> - En retard <span className="text-red-400 font-bold">{fmt(unpaid.overdue_amount)}</span></>}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {unpaid.invoices.length === 0 ? (
                  <p className="text-center py-8 text-zinc-500">Aucune facture impayee</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-2 px-3 text-zinc-400">N facture</th>
                          <th className="text-left py-2 px-3 text-zinc-400">Date</th>
                          <th className="text-left py-2 px-3 text-zinc-400">Client</th>
                          <th className="text-right py-2 px-3 text-zinc-400">Montant</th>
                          <th className="text-right py-2 px-3 text-zinc-400">Retard</th>
                          <th className="text-center py-2 px-3 text-zinc-400">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unpaid.invoices.map((inv) => (
                          <tr key={inv.id} className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${inv.is_overdue ? 'bg-red-950/20' : ''}`}>
                            <td className="py-2 px-3 font-mono text-white">{inv.invoice_number || inv.id?.slice(0, 8)}</td>
                            <td className="py-2 px-3 text-zinc-300">{(inv.created_at || '').slice(0, 10)}</td>
                            <td className="py-2 px-3 text-zinc-300">
                              <p>{inv.user_name}</p>
                              <p className="text-zinc-500 text-xs">{inv.user_email}</p>
                            </td>
                            <td className="py-2 px-3 text-right text-white font-bold">{fmt(inv.total)}</td>
                            <td className="py-2 px-3 text-right">
                              {inv.is_overdue
                                ? <span className="text-red-400 font-bold">{inv.days_late}j</span>
                                : <span className="text-zinc-500">a echoir</span>}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <Badge className={`${STATUS_COLORS[inv.status] || 'bg-zinc-600'} text-white`}>
                                {STATUS_LABELS[inv.status] || inv.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clients" className="mt-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Top clients (CA realise)</CardTitle>
              </CardHeader>
              <CardContent>
                {topClients.length === 0 ? (
                  <p className="text-center py-8 text-zinc-500">Aucun client avec factures payees</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-2 px-3 text-zinc-400">#</th>
                          <th className="text-left py-2 px-3 text-zinc-400">Client</th>
                          <th className="text-right py-2 px-3 text-zinc-400">Nb factures</th>
                          <th className="text-right py-2 px-3 text-zinc-400">Total CA</th>
                          <th className="text-left py-2 px-3 text-zinc-400">Derniere</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topClients.map((c, idx) => (
                          <tr key={c.email || idx} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                            <td className="py-2 px-3 text-zinc-400 font-mono">{idx + 1}</td>
                            <td className="py-2 px-3 text-zinc-300">
                              <p className="font-medium">{c.name || '-'}</p>
                              <p className="text-zinc-500 text-xs">{c.email}</p>
                            </td>
                            <td className="py-2 px-3 text-right text-zinc-300">{c.count}</td>
                            <td className="py-2 px-3 text-right text-white font-bold">{fmt(c.total_ttc)}</td>
                            <td className="py-2 px-3 text-zinc-500 text-xs">{(c.last_invoice || '').slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
